import { Parser } from 'expr-eval-fork';

// Compound metric expressions are stored with raw metric IDs as terms
// (e.g. "abc-123 / def-456 * 100"). In the form, each input metric is shown
// as a letter instead (A = metricIds[0], B = metricIds[1]...), so the user
// edits "A / B * 100". The ID form remains the source of truth: letters are
// derived from the input metric order, so removing an input re-letters the
// expression consistently.

const MAX_LETTERS = 26;

// A lone capital letter, not part of a longer word or function name. Digits
// may sit next to it, so "2A" still resolves.
const LETTER_TOKEN = /(?<![A-Za-z_])([A-Z])(?![A-Za-z_])/g;

export const getMetricLetter = (index: number): string | undefined =>
  index < MAX_LETTERS ? String.fromCharCode(65 + index) : undefined;

const getLetterIndex = (letter: string) => letter.charCodeAt(0) - 65;

export const toLetterExpression = ({
  expression,
  metricIds,
}: {
  expression: string;
  metricIds: string[];
}): string => {
  // Replace longest IDs first so a shorter ID can't corrupt a longer one it
  // happens to be a substring of (IDs may contain "-", so no tokenising).
  return metricIds
    .map((metricId, index) => ({ metricId, letter: getMetricLetter(index) }))
    .sort((a, b) => b.metricId.length - a.metricId.length)
    .reduce(
      (expr, { metricId, letter }) =>
        letter ? expr.split(metricId).join(letter) : expr,
      expression,
    );
};

export const toIdExpression = ({
  letterExpression,
  metricIds,
}: {
  letterExpression: string;
  metricIds: string[];
}): string =>
  letterExpression.replace(LETTER_TOKEN, (letter) => {
    const metricId = metricIds[getLetterIndex(letter)];
    return metricId ?? letter;
  });

export type ExpressionPart =
  | { type: 'text'; text: string }
  | { type: 'metric'; letter: string; metricIndex: number };

// Splits a letter expression into text and metric references, for rendering
export const toExpressionParts = ({
  letterExpression,
  metricCount,
}: {
  letterExpression: string;
  metricCount: number;
}): ExpressionPart[] => {
  const parts: ExpressionPart[] = [];
  let lastIndex = 0;
  letterExpression.replace(LETTER_TOKEN, (letter, _group, offset: number) => {
    const metricIndex = getLetterIndex(letter);
    if (metricIndex >= metricCount) {
      return letter;
    }
    if (offset > lastIndex) {
      parts.push({
        type: 'text',
        text: letterExpression.slice(lastIndex, offset),
      });
    }
    parts.push({ type: 'metric', letter, metricIndex });
    lastIndex = offset + letter.length;
    return letter;
  });
  if (lastIndex < letterExpression.length) {
    parts.push({ type: 'text', text: letterExpression.slice(lastIndex) });
  }

  return parts;
};

const describeLetterRange = (metricCount: number) => {
  if (metricCount === 1) {
    return 'A';
  }

  return `A–${getMetricLetter(Math.min(metricCount, MAX_LETTERS) - 1)}`;
};

const getParseError = (letterExpression: string): string | undefined => {
  const withValues = letterExpression.replace(LETTER_TOKEN, '1');
  try {
    const parser = new Parser();
    parser.functions.annualized = (metricValue: number) => metricValue;
    parser.evaluate(withValues);
    return undefined;
  } catch (e) {
    return e instanceof Error ? e.message : String(e);
  }
};

/**
 * Explains why an expression is invalid, in terms of what the user typed.
 * Only call this when the stored expression has failed validation.
 */
export const getExpressionError = ({
  letterExpression,
  metricIds,
  removedMetricNames,
}: {
  letterExpression: string;
  metricIds: string[];
  // Metrics removed from the inputs, keyed by ID, whose ID is still in the
  // expression
  removedMetricNames: { [metricId: string]: string };
}): string | undefined => {
  if (letterExpression.trim() === '') {
    return undefined;
  }

  const removedName = Object.entries(removedMetricNames).find(([metricId]) =>
    letterExpression.includes(metricId),
  );
  if (removedName) {
    return `"${removedName[1]}" was removed from the input metrics but is still in the expression.`;
  }

  const unknownLetter = (letterExpression.match(LETTER_TOKEN) ?? []).find(
    (letter) => getLetterIndex(letter) >= metricIds.length,
  );
  if (unknownLetter) {
    return metricIds.length === 0
      ? `There is no metric "${unknownLetter}". Add an input metric first.`
      : `There is no metric "${unknownLetter}". Use ${describeLetterRange(
          metricIds.length,
        )}.`;
  }

  const opening = (letterExpression.match(/\(/g) ?? []).length;
  const closing = (letterExpression.match(/\)/g) ?? []).length;
  if (opening !== closing) {
    return `Brackets don't match: ${opening} opening and ${closing} closing.`;
  }

  if (/[+\-*/^%]\s*$/.test(letterExpression)) {
    return 'The expression ends with an operator.';
  }

  const parseError = getParseError(letterExpression);
  const undefinedVariable = parseError
    ? /undefined variable: (\S+)/.exec(parseError)
    : null;
  if (undefinedVariable) {
    return `"${undefinedVariable[1]}" isn't a metric. Refer to input metrics by their letter (A, B…).`;
  }

  return "This isn't a valid expression. Check for a missing operator between terms.";
};
